import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorageState } from '@ole-ui/ole-ui-components';
import AIAssistantCourseStore from 'components/Course/AiAssist/store';

type SidebarPosition = 'left' | 'right';
type RestoreSidebar = 'toc' | 'ai' | null;

type UseSidebarStateArgs = {
  /**
   * Feature flag that globally enables/disables AI Assistant UI and behavior.
   * When disabled, we should ensure AI sidebar is closed and we should not restore it.
   */
  isAiAssistEnabled: boolean;
  /**
   * AI Assistant store for the course. Used to open/close the AI sidebar.
   */
  aiAssistantStore: AIAssistantCourseStore;
};

/**
 * Centralized state machine for coordinating external sidebars in CourseViewV2.
 *
 * We have two "external" sidebars that render via portals into placeholder divs:
 * - Sidebar ToC (position: left or right; persisted in localStorage)
 * - AI Assistant sidebar (always rendered in the right placeholder; gated by feature flag)
 *
 * Invariants:
 * - Only ONE of these sidebars should be visible at a time on desktop.
 * - If opening one sidebar closes the other, we remember which one to restore when the active sidebar closes.
 * - User preferences (ToC position and AI quick menu visibility) are persisted in localStorage.
 */
export default function useSidebarState({
  isAiAssistEnabled,
  aiAssistantStore,
}: UseSidebarStateArgs) {
  // Portal targets
  const sideToCRefLeft = useRef<HTMLDivElement>(null);
  const sideToCRefRight = useRef<HTMLDivElement>(null);

  // ToC open/close state
  const [isSideToCActive, setSideToCActiveInternal] = useState(false);

  // Preferences
  const [sidebarPosition, setSidebarPosition] =
    useLocalStorageState<SidebarPosition>(
      'course-hud-sidebar-toc-position',
      'left',
    );

  const [isAiQuickMenuEnabled, setIsAiQuickMenuEnabled] =
    useLocalStorageState<boolean>('ole-ai-quick-menu-enabled', true);

  // Which sidebar should be restored after the currently active one closes.
  const [restoreSidebarAfterClose, setRestoreSidebarAfterClose] =
    useState<RestoreSidebar>(null);

  // AI sidebar is effectively open only when the feature flag is enabled.
  const isAiAssistSidebarOpen = isAiAssistEnabled && aiAssistantStore.isOpen;

  const prevAiSidebarOpenRef = useRef(isAiAssistSidebarOpen);
  const prevTocActiveRef = useRef(isSideToCActive);

  /**
   * If the feature flag is turned off, ensure we don't keep AI state open or queued for restore.
   * (Matches current behavior in index.tsx.)
   */
  useEffect(() => {
    if (isAiAssistEnabled) return;

    if (aiAssistantStore.isOpen) {
      aiAssistantStore.setIsOpen(false);
    }

    if (restoreSidebarAfterClose === 'ai') {
      setRestoreSidebarAfterClose(null);
    }
  }, [aiAssistantStore, isAiAssistEnabled, restoreSidebarAfterClose]);

  /**
   * AI sidebar open/close transitions:
   * - When AI opens while ToC is active -> close ToC and remember to restore it.
   * - When AI closes -> if it displaced ToC, restore ToC.
   */
  useEffect(() => {
    const wasOpen = prevAiSidebarOpenRef.current;

    // AI sidebar just opened: close TOC and restore it when AI is dismissed.
    if (!wasOpen && isAiAssistSidebarOpen && isSideToCActive) {
      setRestoreSidebarAfterClose('toc');
      setSideToCActiveInternal(false);
    }

    // AI sidebar just closed: restore TOC if it was displaced by AI.
    if (
      wasOpen &&
      !isAiAssistSidebarOpen &&
      restoreSidebarAfterClose === 'toc'
    ) {
      setSideToCActiveInternal(true);
      setRestoreSidebarAfterClose(null);
    }

    prevAiSidebarOpenRef.current = isAiAssistSidebarOpen;
  }, [isAiAssistSidebarOpen, isSideToCActive, restoreSidebarAfterClose]);

  /**
   * ToC open/close transitions:
   * - When ToC closes -> if it displaced AI, restore AI (only if feature flag enabled).
   */
  useEffect(() => {
    const wasOpen = prevTocActiveRef.current;

    // TOC sidebar just closed: restore AI if it was displaced by TOC.
    if (
      isAiAssistEnabled &&
      wasOpen &&
      !isSideToCActive &&
      restoreSidebarAfterClose === 'ai'
    ) {
      aiAssistantStore.setIsOpen(true);
      setRestoreSidebarAfterClose(null);
    }

    prevTocActiveRef.current = isSideToCActive;
  }, [
    aiAssistantStore,
    isAiAssistEnabled,
    isSideToCActive,
    restoreSidebarAfterClose,
  ]);

  /**
   * Public setter used by CourseHUD.
   * If user opens ToC while AI is open, close AI and remember to restore it after ToC is dismissed.
   */
  const setSideToCActive = useCallback(
    (isActive: boolean) => {
      if (isActive && isAiAssistSidebarOpen) {
        setRestoreSidebarAfterClose('ai');
        aiAssistantStore.setIsOpen(false);
      }
      setSideToCActiveInternal(isActive);
    },
    [aiAssistantStore, isAiAssistSidebarOpen],
  );

  const sideToCRef = useMemo(
    () => (sidebarPosition === 'left' ? sideToCRefLeft : sideToCRefRight),
    [sidebarPosition],
  );

  // AI Assist sidebar always portals into the right placeholder.
  // Temporary until we don't have the setting available
  const aiAssistRef = sideToCRefRight;

  return {
    sideToCRefLeft,
    sideToCRefRight,
    sideToCRef,
    aiAssistRef,
    sidebarPosition,
    setSidebarPosition,
    isSideToCActive,
    setSideToCActive,
    isAiQuickMenuEnabled,
    setIsAiQuickMenuEnabled,
  };
}
